import { useAlerts } from "components/Alerts/AlertProvider";
import { JobStatus, JobTask } from "generated-graphql/graphql";
import { DialogType, useMinimizedDialogActionsOptional } from "providers/minimizedDialogs";
import { useToken } from "providers/token";
import { useCallback, useEffect, useState } from "react";

import { useCheckForFile } from "./useCheckForFile";
import { useLongPollingForJob } from "./useLongPollingForJob";

export interface ExportParams {
  tenantId?: string;
  fileName?: string;
  facilityId?: string;
  reportingYear?: number | string;
  /** Export type string used for the export endpoint - see
   * `apps/api/src/download/index.ts` */
  exportType: string;
  filter?: Record<string, any>;
  /**
   * Column visibility model from the grid settings.
   * Keys are field names, values are booleans (false = hidden).
   * Used to filter which columns are included in the export.
   */
  columnVisibility?: Record<string, boolean>;
  /**
   * Tray-mode only (see `progressMode` on `useExportData`): title shown in
   * the minimized-dialogs tray chip for this export. Ignored in the default
   * "inline" mode.
   */
  trayTitle?: string;
}

export type ExportStatus = "idle" | "loading" | "ready_to_download";

export type StartExportResult = "queued" | "cached" | "error";

export interface UseExportDataReturn {
  startExport: (params: ExportParams, onComplete?: () => void) => Promise<StartExportResult>;
  downloadFile: () => void;
  clearCache: () => void;
  status: ExportStatus;
}

export function generateSpreadsheetFilename(prefix: string): string {
  const cleaned = prefix.replace(/[&]+/g, "");
  if (cleaned.toLowerCase().endsWith(".xlsx")) {
    return cleaned;
  }
  return `${cleaned}-${new Date().toISOString()}.xlsx`;
}

// Hook for caching export results, so that subsequent clicks on the same export button don't trigger a new export
const useExportCache = () => {
  const [cachedExports, setCachedExports] = useState<Map<string, string>>(new Map());

  // Generate a cache key from export parameters
  const generateCacheKey = useCallback((params: ExportParams) => {
    // Create a deterministic key from the parameters
    const keyData = {
      exportType: params.exportType,
      tenantId: params.tenantId,
      facilityId: params.facilityId,
      reportingYear: params.reportingYear,
      filter: params.filter,
      columnVisibility: params.columnVisibility,
      // Note: We don't include fileName in cache key since it contains timestamp
    };
    return JSON.stringify(keyData);
  }, []);

  const getCachedExport = useCallback(
    (params: ExportParams) => {
      const cacheKey = generateCacheKey(params);
      return cachedExports.get(cacheKey);
    },
    [cachedExports, generateCacheKey]
  );

  const setCachedExport = useCallback(
    (params: ExportParams, signedUrl: string) => {
      const cacheKey = generateCacheKey(params);
      setCachedExports((prev) => new Map(prev.set(cacheKey, signedUrl)));
    },
    [generateCacheKey]
  );

  const clearCache = useCallback(() => {
    setCachedExports(new Map());
  }, []);

  return {
    getCachedExport,
    setCachedExport,
    clearCache,
  };
};

/**
 * This hook helps with generating of export files from the server.
 */
export const useExportData = ({
  timeoutInSeconds,
  autoDownload = true,
  disableCache = false,
  progressMode = "inline",
}: {
  timeoutInSeconds?: number;
  autoDownload?: boolean;
  /**
   * Skip the by-params result cache so every export queues a fresh job. Use for
   * exports over mutable data where the same filter can yield different rows
   * between clicks (e.g. a live review queue whose assignments change).
   */
  disableCache?: boolean;
  /**
   * "inline" (default): unchanged legacy behavior — this hook polls the job
   * itself and resolves `status`/`downloadFile()`.
   *
   * "tray": on a cache miss, hand the queued job off to the global
   * minimized-dialogs tray (`JobExportProgressDialog`) instead of polling
   * internally. The tray dialog owns progress from there — it's spawned
   * already minimized so a one-click export isn't interrupted by a modal.
   * Cache hits are unaffected either way (still an instant download, no
   * dialog).
   */
  progressMode?: "inline" | "tray";
}): UseExportDataReturn => {
  const { getIdTokenRefreshIfExpired } = useToken();
  const alerts = useAlerts();
  // Actions-only + optional, both load-bearing for the ~40 inline call
  // sites this hook backs: the actions object is memoized once by the
  // provider and never changes identity, so unrelated tray dialogs
  // opening/closing elsewhere in the app can't re-render inline consumers
  // or churn startExport's identity; and the accessor returns undefined
  // instead of throwing for consumers rendered without
  // MinimizedDialogsProvider (isolated unit tests, mainly). Only tray mode
  // ever dispatches through it.
  const minimizedDialogs = useMinimizedDialogActionsOptional();
  const [status, setStatus] = useState<ExportStatus>("idle");
  const [signedUrl, setSignedUrl] = useState<string | undefined>(undefined);
  const [onCompleteCallback, setOnCompleteCallback] = useState<(() => void) | undefined>(undefined);
  const [currentParams, setCurrentParams] = useState<ExportParams | undefined>(undefined);

  const { polling, result, startPolling } = useLongPollingForJob({
    timeoutInSeconds: timeoutInSeconds ?? 900, // 15 minutes
  });

  const { fetchFile } = useCheckForFile();
  const { getCachedExport, setCachedExport, clearCache } = useExportCache();

  // Generate endpoint URL for a given export job
  const generateEndpoint = useCallback((params: ExportParams) => {
    const fullFileName = generateSpreadsheetFilename(params.fileName ?? params.exportType);
    const urlParts = [
      `/download/${params.exportType}?filename=${encodeURIComponent(fullFileName)}`,
      params.tenantId ? `&tenantId=${params.tenantId.trim()}` : "",
      params.facilityId ? `&facilityId=${params.facilityId.trim()}` : "",
      params.reportingYear ? `&reportingYear=${params.reportingYear}` : "",
      params.filter ? `&filter=${encodeURIComponent(JSON.stringify(params.filter))}` : "",
      params.columnVisibility
        ? `&columnVisibility=${encodeURIComponent(JSON.stringify(params.columnVisibility))}`
        : "",
    ];
    return urlParts.join("");
  }, []);

  // Handle job completion from polling
  const handleJobComplete = useCallback(
    async (params: ExportParams) => {
      if (result.pollingTimedOut) {
        setStatus("idle");
        alerts.error("Export job timed out");
        return;
      }

      if (!result.job) {
        return;
      }

      if (result.job.status === JobStatus.Succeeded) {
        const fileSignedUrl = await fetchFile(result.job.output.downloadKeys[0]);

        if (fileSignedUrl) {
          setSignedUrl(fileSignedUrl);
          setStatus("ready_to_download");

          // Cache the result for future use (unless caching is disabled)
          if (!disableCache) {
            setCachedExport(params, fileSignedUrl);
          }

          // Auto-download if enabled
          if (autoDownload) {
            // Trigger download immediately
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = fileSignedUrl;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Reset state after auto-download
            setStatus("idle");
            setSignedUrl(undefined);
            setOnCompleteCallback(undefined);
            setCurrentParams(undefined);
          }

          // Call the onComplete callback if provided
          if (onCompleteCallback) {
            onCompleteCallback();
          }
        } else {
          setStatus("idle");
          alerts.error("Failed to generate export file");
        }
      } else if (result.job.status === JobStatus.Failed) {
        setStatus("idle");
        const actionName = result.job.task === JobTask.GeneratePdf ? "PDF" : "export file";
        alerts.error(`Failed to generate ${actionName}`);
      }
    },
    [result, alerts, fetchFile, onCompleteCallback, setCachedExport, autoDownload, disableCache]
  );

  // Handle job completion when polling result changes
  useEffect(() => {
    if (result.job && currentParams && !polling) {
      handleJobComplete(currentParams);
    }
  }, [result, polling, currentParams, handleJobComplete]);

  const startExport = useCallback(
    async (params: ExportParams, onComplete?: () => void) => {
      const shouldAutoDownload = autoDownload;

      // Check if we have a cached export for these parameters (unless caching is
      // disabled, e.g. for exports over a mutable queue).
      const cachedSignedUrl = disableCache ? undefined : getCachedExport(params);
      if (cachedSignedUrl) {
        // Use cached result
        setSignedUrl(cachedSignedUrl);
        setStatus("ready_to_download");
        setOnCompleteCallback(() => onComplete);

        // Auto-download from cache if enabled
        if (shouldAutoDownload) {
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = cachedSignedUrl;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          // Reset state after auto-download
          setStatus("idle");
          setSignedUrl(undefined);
          setOnCompleteCallback(undefined);
          setCurrentParams(undefined);
        }

        // Call the onComplete callback if provided
        if (onComplete) {
          onComplete();
        }
        return "cached";
      }

      // Reset state for new export
      setStatus("loading");
      setSignedUrl(undefined);
      setOnCompleteCallback(() => onComplete);
      setCurrentParams(params);

      const apiUrl = import.meta.env.VITE_API_URL;
      const idToken = await getIdTokenRefreshIfExpired();
      const authorization = `Bearer ${idToken || ""}`;
      const endpoint = generateEndpoint(params);

      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          headers: {
            authorization,
          },
        });

        if (response.status >= 400) {
          setStatus("idle");
          alerts.error("Error generating export file");
          return "error";
        } else {
          const { jobId: serverJobId } = await response.json();
          if (progressMode === "tray" && minimizedDialogs) {
            // The JobExportProgressDialog owns polling for this job from
            // here (see components/JobExportProgressDialog.tsx) — this
            // hook's job is done once the tray chip is spawned, so no
            // internal polling and no toast (the chip is the feedback).
            //
            // Trade-off: since the by-params cache above is only ever
            // populated by handleJobComplete's polling result, skipping
            // that polling here also means we skip the cache write. An
            // identical re-export will re-queue a job rather than reuse a
            // cached signed URL. Acceptable — and since the dialog is keyed
            // by jobId, an in-flight duplicate just re-highlights the
            // existing tray chip rather than stacking a new one.
            minimizedDialogs.createDialog({
              id: serverJobId,
              dialogType: DialogType.JobExportProgress,
              mode: "minimizedInTray",
              props: {
                id: serverJobId,
                jobId: serverJobId,
                title: params.trayTitle ?? params.exportType,
                // EXPORT_DATA jobs never send completion emails — the dialog
                // copy must not promise one (its default, stated explicitly).
                notifiesByEmail: false,
              },
            });
            setStatus("idle");
          } else {
            // Also the fallback for a "tray" caller rendered without
            // MinimizedDialogsProvider (shouldn't happen in the app itself —
            // see the comment on `minimizedDialogs` above).
            startPolling(serverJobId);
          }
          return "queued";
        }
      } catch (err) {
        console.error(err);
        setStatus("idle");
        alerts.error("Error generating export file");
        return "error";
      }
    },
    [
      getIdTokenRefreshIfExpired,
      alerts,
      generateEndpoint,
      startPolling,
      getCachedExport,
      autoDownload,
      disableCache,
      progressMode,
      minimizedDialogs,
    ]
  );

  const downloadFile = useCallback(() => {
    if (!signedUrl) return;

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = signedUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Reset state after download
    setStatus("idle");
    setSignedUrl(undefined);
    setOnCompleteCallback(undefined);
    setCurrentParams(undefined);
  }, [signedUrl]);

  return {
    startExport,
    downloadFile,
    clearCache,
    status,
  };
};
